/**
 * Performance Utilities for Blog Pages
 * Implements lazy loading, image optimization, and performance monitoring
 */

/**
 * Lazy loading configuration
 */
const LAZY_LOADING_CONFIG = {
  rootMargin: '50px 0px',
  threshold: 0.01,
  loadingPlaceholder: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+'
};

/**
 * Image optimization utilities
 */
export const ImageOptimizer = {
  /**
   * Generate optimized image URL with parameters
   */
  getOptimizedImageUrl(originalUrl, options = {}) {
    if (!originalUrl) return LAZY_LOADING_CONFIG.loadingPlaceholder;
    
    const {
      width,
      height,
      quality = 85,
      format = 'auto'
    } = options;
    
    // If it's already a data URL or external URL, return as-is
    if (originalUrl.startsWith('data:') || originalUrl.startsWith('http')) {
      return originalUrl;
    }
    
    // For local images, we could implement URL parameters for optimization
    // This would typically integrate with a CDN or image optimization service
    let optimizedUrl = originalUrl;
    
    const params = new URLSearchParams();
    if (width) params.append('w', width);
    if (height) params.append('h', height);
    if (quality !== 85) params.append('q', quality);
    if (format !== 'auto') params.append('f', format);
    
    if (params.toString()) {
      optimizedUrl += `?${params.toString()}`;
    }
    
    return optimizedUrl;
  },

  /**
   * Generate responsive image srcSet
   */
  generateSrcSet(originalUrl, sizes = [400, 800, 1200]) {
    if (!originalUrl) return '';
    
    return sizes
      .map(size => `${this.getOptimizedImageUrl(originalUrl, { width: size })} ${size}w`)
      .join(', ');
  },

  /**
   * Generate sizes attribute for responsive images
   */
  generateSizes(breakpoints = {
    mobile: '(max-width: 768px) 100vw',
    tablet: '(max-width: 1024px) 50vw',
    desktop: '33vw'
  }) {
    return Object.values(breakpoints).join(', ');
  }
};

/**
 * Lazy loading implementation using Intersection Observer
 */
export class LazyImageLoader {
  constructor(options = {}) {
    this.config = { ...LAZY_LOADING_CONFIG, ...options };
    this.observer = null;
    this.loadedImages = new Set();
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        {
          rootMargin: this.config.rootMargin,
          threshold: this.config.threshold
        }
      );
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.loadImage(entry.target);
        this.observer.unobserve(entry.target);
      }
    });
  }

  loadImage(img) {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    
    if (!src) return;

    // Create a new image to preload
    const imageLoader = new Image();
    
    imageLoader.onload = () => {
      // Image loaded successfully
      img.src = src;
      if (srcset) img.srcset = srcset;
      
      img.classList.remove('lazy-loading');
      img.classList.add('lazy-loaded');
      
      this.loadedImages.add(img);
      
      // Dispatch custom event
      img.dispatchEvent(new CustomEvent('lazyloaded', {
        detail: { src, srcset }
      }));
    };
    
    imageLoader.onerror = () => {
      // Handle error - show placeholder or fallback
      img.classList.remove('lazy-loading');
      img.classList.add('lazy-error');
      
      // Dispatch error event
      img.dispatchEvent(new CustomEvent('lazyerror', {
        detail: { src, srcset }
      }));
    };
    
    // Start loading
    if (srcset) imageLoader.srcset = srcset;
    imageLoader.src = src;
  }

  observe(img) {
    if (!this.observer) {
      // Fallback for browsers without Intersection Observer
      this.loadImage(img);
      return;
    }
    
    img.classList.add('lazy-loading');
    this.observer.observe(img);
  }

  unobserve(img) {
    if (this.observer) {
      this.observer.unobserve(img);
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  getStats() {
    return {
      loadedCount: this.loadedImages.size,
      observerSupported: !!this.observer
    };
  }
}

// Global lazy loader instance
let globalLazyLoader = null;

/**
 * Initialize lazy loading for the application
 */
export const initializeLazyLoading = (options = {}) => {
  if (!globalLazyLoader) {
    globalLazyLoader = new LazyImageLoader(options);
  }
  return globalLazyLoader;
};

/**
 * Setup lazy loading for images in a container
 */
export const setupLazyLoading = (container = document) => {
  const lazyLoader = initializeLazyLoading();
  
  // Find all images with data-src attribute
  const lazyImages = container.querySelectorAll('img[data-src]');
  
  lazyImages.forEach(img => {
    lazyLoader.observe(img);
  });
  
  return lazyImages.length;
};

/**
 * Create lazy image element
 */
export const createLazyImage = (src, options = {}) => {
  const {
    alt = '',
    className = '',
    width,
    height,
    sizes,
    placeholder = LAZY_LOADING_CONFIG.loadingPlaceholder
  } = options;
  
  const img = document.createElement('img');
  
  // Set placeholder
  img.src = placeholder;
  img.dataset.src = src;
  
  // Set responsive attributes
  if (sizes) {
    const srcSet = ImageOptimizer.generateSrcSet(src);
    if (srcSet) {
      img.dataset.srcset = srcSet;
      img.sizes = ImageOptimizer.generateSizes();
    }
  }
  
  // Set other attributes
  img.alt = alt;
  img.className = className;
  if (width) img.width = width;
  if (height) img.height = height;
  
  // Add loading class
  img.classList.add('lazy-loading');
  
  return img;
};

/**
 * Performance monitoring utilities
 */
export const PerformanceMonitor = {
  /**
   * Measure page load performance
   */
  measurePageLoad() {
    if ('performance' in window) {
      const entries = performance.getEntriesByType('navigation');
      if (!entries || entries.length === 0) return null;
      
      const navigation = entries[0];
      
      // Helper to safely calculate timing (returns 0 if NaN or negative)
      const safeTiming = (end, start) => {
        const result = (end || 0) - (start || 0);
        return isNaN(result) || result < 0 ? 0 : result;
      };
      
      return {
        domContentLoaded: safeTiming(navigation.domContentLoadedEventEnd, navigation.domContentLoadedEventStart),
        loadComplete: safeTiming(navigation.loadEventEnd, navigation.loadEventStart),
        totalTime: safeTiming(navigation.loadEventEnd, navigation.fetchStart),
        dnsLookup: safeTiming(navigation.domainLookupEnd, navigation.domainLookupStart),
        tcpConnect: safeTiming(navigation.connectEnd, navigation.connectStart),
        serverResponse: safeTiming(navigation.responseEnd, navigation.requestStart),
        domProcessing: safeTiming(navigation.domComplete, navigation.domInteractive)
      };
    }
    return null;
  },

  /**
   * Measure resource loading performance
   */
  measureResourceLoading() {
    if ('performance' in window) {
      const resources = performance.getEntriesByType('resource');
      
      const imageResources = resources.filter(resource => 
        resource.name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
      );
      
      const totalImageSize = imageResources.reduce((total, resource) => 
        total + (resource.transferSize || 0), 0
      );
      
      return {
        totalResources: resources.length,
        imageResources: imageResources.length,
        totalImageSize,
        averageImageLoadTime: imageResources.length > 0 
          ? imageResources.reduce((total, resource) => total + resource.duration, 0) / imageResources.length
          : 0
      };
    }
    return null;
  },

  /**
   * Monitor Core Web Vitals
   */
  measureCoreWebVitals() {
    const vitals = {};
    
    if (!('PerformanceObserver' in window)) {
      console.warn('PerformanceObserver not supported');
      return vitals;
    }

    // Get supported entry types
    const supportedTypes = PerformanceObserver.supportedEntryTypes || [];
    
    // Largest Contentful Paint (LCP)
    if (supportedTypes.includes('largest-contentful-paint')) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          vitals.lcp = lastEntry.startTime;
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.warn('LCP measurement not supported');
      }
    }
    
    // First Input Delay (FID)
    if (supportedTypes.includes('first-input')) {
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            vitals.fid = entry.processingStart - entry.startTime;
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.warn('FID measurement not supported');
      }
    }
    
    // Cumulative Layout Shift (CLS) - Not supported in Firefox
    if (supportedTypes.includes('layout-shift')) {
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          vitals.cls = clsValue;
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.warn('CLS measurement not supported');
      }
    }
    
    return vitals;
  },

  /**
   * Log performance metrics
   */
  logPerformanceMetrics() {
    const pageLoad = this.measurePageLoad();
    const resources = this.measureResourceLoading();
    const vitals = this.measureCoreWebVitals();
    
    console.group('Performance Metrics');
    if (pageLoad) {
      console.log('Page Load:', pageLoad);
    }
    if (resources) {
      console.log('Resources:', resources);
    }
    if (vitals) {
      console.log('Core Web Vitals:', vitals);
    }
    console.groupEnd();
    
    return { pageLoad, resources, vitals };
  }
};

/**
 * Debounce utility for performance optimization
 */
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

/**
 * Throttle utility for performance optimization
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Preload critical resources
 */
export const preloadCriticalResources = (resources = []) => {
  resources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource.href;
    link.as = resource.as || 'image';
    if (resource.type) link.type = resource.type;
    document.head.appendChild(link);
  });
};

/**
 * Setup performance monitoring
 */
export const setupPerformanceMonitoring = () => {
  // Monitor performance on page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      PerformanceMonitor.logPerformanceMetrics();
    }, 1000);
  });
  
  // Monitor lazy loading performance
  document.addEventListener('lazyloaded', (event) => {
    console.log('Image lazy loaded:', event.detail.src);
  });
  
  document.addEventListener('lazyerror', (event) => {
    console.warn('Image lazy load error:', event.detail.src);
  });
};

export default {
  ImageOptimizer,
  LazyImageLoader,
  initializeLazyLoading,
  setupLazyLoading,
  createLazyImage,
  PerformanceMonitor,
  debounce,
  throttle,
  preloadCriticalResources,
  setupPerformanceMonitoring
};